import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { canAccessBilling } from '../../utils/permissions';
import { deviceLimitState, upgradeHref } from '../../utils/subscription';

interface DeviceCountProps {
  subscriptionId: string;
  plan: string | null;
  current: number;
  max: number;
}

export default function DeviceCount({ subscriptionId, plan, current, max }: DeviceCountProps) {
  const { user } = useAuth();
  const percentage = (current / max) * 100;
  const limitState = deviceLimitState(current, max);
  const isOverLimit = limitState === 'over';
  const isNearLimit = limitState === 'near';
  const canUpgrade = canAccessBilling(user);
  const planPhrase = plan ? `your ${plan} plan` : 'your plan';

  const upgradeCta = canUpgrade ? (
    <Link to={upgradeHref(subscriptionId)} className="underline font-medium">
      Upgrade plan
    </Link>
  ) : (
    'Ask an account admin to upgrade this plan.'
  );

  return (
    <div>
      <span className={isOverLimit ? 'text-red-600 font-semibold' : isNearLimit ? 'text-amber-600 font-semibold' : ''}>
        {current.toLocaleString()}/{max.toLocaleString()}
      </span>
      {isOverLimit && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">
            <strong>Over device limit:</strong> this controller is managing{' '}
            {current.toLocaleString()} of the {max.toLocaleString()} devices included in{' '}
            {planPhrase}. Performance and stability suffer above the limit.
          </p>
          <p className="text-sm text-red-700 mt-1">{upgradeCta}</p>
          <p className="text-xs text-red-700 mt-1">
            Need help? <a href="mailto:support@cloudunifi.com" className="underline">support@cloudunifi.com</a>
          </p>
        </div>
      )}
      {isNearLimit && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-700">
            <strong>Approaching device limit:</strong> you're using {percentage.toFixed(0)}% of{' '}
            {planPhrase}'s {max.toLocaleString()}-device capacity.
          </p>
          <p className="text-sm text-amber-700 mt-1">{upgradeCta}</p>
          <p className="text-xs text-amber-700 mt-1">
            Need help? <a href="mailto:support@cloudunifi.com" className="underline">support@cloudunifi.com</a>
          </p>
        </div>
      )}
    </div>
  );
}
